import { useState } from "react";
import { Dialog, DialogContent, Typography } from "@mui/material";
import CustomPrimaryButton from "@fuse/core/CustomPrimaryButton/CustomPrimaryButton";
import useThemeMediaQuery from "@fuse/hooks/useThemeMediaQuery";
import RemoveRecordDialog from "./RemoveRecordDialog";

interface RecoverActivateModalProps {
	petName: string;
	chip: string;
	email: string;
	onActivate: () => void;
	onRemove?: () => void;
}

function RecoverActivateModal({ petName: rawPetName, chip, email, onActivate, onRemove }: RecoverActivateModalProps) {
	const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down("sm"));
	const petName = rawPetName ? rawPetName.charAt(0).toUpperCase() + rawPetName.slice(1).toLowerCase() : "";
	const [view, setView] = useState<"activate" | "remove">("activate");

	const content = (
		<div className={`flex flex-col ${isMobile ? "gap-7" : "gap-12"}`}>
			{/* Logo */}
			<div className="flex justify-center">
				<img src="assets/img/checkout-logo.svg" alt="Peeva" style={{ width: 64 }} />
			</div>

			{/* Subtitle */}
			<div className="flex flex-col items-center">
				<Typography variant="body2" align="center" color="text.secondary" fontWeight={700} sx={{ fontSize: 14 }}>
					Secure record lookup - fetch.peeva.co
				</Typography>
			</div>

			{/* Pet Record Box */}
			<div style={{ border: "3px solid #B0B0B0", borderRadius: 16, padding: "16px 20px" }}>
				<div className="flex items-center justify-center gap-5">
					<img src="assets/img/lock.png" alt="" style={{ width: 64, height: "auto", flexShrink: 0 }} />
					<div className="flex flex-col">
						<Typography sx={{ fontSize: 18, color: "#6B7280", fontWeight: 500, lineHeight: 1.35 }}>
							Pet Record: <Typography component="span" sx={{ fontSize: 18, color: "#6B7280", fontWeight: 800 }}>{petName}</Typography>
						</Typography>
						<Typography sx={{ fontSize: 18, color: "#6B7280", fontWeight: 500, lineHeight: 1.35 }}>
							Microchip ID: <Typography component="span" sx={{ fontSize: 18, color: "#6B7280", fontWeight: 800 }}>{chip}</Typography>
						</Typography>
						<Typography sx={{ fontSize: 18, color: "#52BFF4", fontWeight: 500, lineHeight: 1.35 }}>
							Status: <Typography component="span" sx={{ fontSize: 18, color: "#52BFF4", fontWeight: 800 }}>Not Active</Typography>
						</Typography>
					</div>
				</div>
			</div>

			{/* Pricing */}
			<div className="flex flex-col items-center gap-2" style={{ border: "3px solid #B0B0B0", borderRadius: 16, padding: "16px 20px" }}>
				<Typography align="center" sx={{ fontSize: 26, fontWeight: 800, color: "#6B7280", lineHeight: 1.2 }}>
					Lifetime Enrollment
				</Typography>
				<div style={{ textAlign: "center", lineHeight: 1.2 }}>
					<span style={{ fontSize: 32, fontWeight: 800, textDecoration: "line-through", color: "#6B7280", marginRight: 8 }}>
						$149
					</span>
					<span style={{ fontSize: 32, fontWeight: 800, color: "#52BFF4" }}>
						$104
					</span>{" "}
					<span style={{ fontSize: 18, fontWeight: 600, color: "#6B7280" }}>one-time</span>
				</div>
				<Typography align="center" sx={{ fontSize: 15, fontStyle: "italic", fontWeight: 800, color: "#6B7280" }}>
					30% reduced activation fee applied at checkout
				</Typography>
			</div>

			{/* CTA */}
			<div style={isMobile ? { position: "sticky", bottom: 0, background: "#fff", paddingTop: 8, paddingBottom: 8 } : undefined}>
				<CustomPrimaryButton onClick={onActivate} fullWidth>
					Activate {petName}'s Microchip
				</CustomPrimaryButton>
			</div>

			{/* Remove block */}
			<div className="flex flex-col items-center gap-2">
				<Typography align="center" sx={{ fontSize: 15, fontWeight: 700, color: "#6B7280" }}>
					Do you no longer own this pet?
				</Typography>
				<button
					onClick={() => setView("remove")}
					style={{
						minWidth: 200,
						padding: "10px 48px",
						border: "2px solid #52BFF4",
						color: "#52BFF4",
						background: "transparent",
						borderRadius: 9999,
						fontWeight: 700,
						fontSize: 16,
						cursor: "pointer",
					}}
				>
					Remove
				</button>
			</div>

			{/* Payment icons */}
			<div className="flex justify-center gap-3 flex-wrap">
				{[
					{ src: "assets/images/payment/37.png", alt: "Apple Pay" },
					{ src: "assets/images/payment/38.png", alt: "Google Pay" },
					{ src: "assets/images/payment/39.png", alt: "Visa" },
					{ src: "assets/images/payment/40.png", alt: "Mastercard" },
					{ src: "assets/images/payment/44.png", alt: "Klarna" },
				].map(({ src, alt }) => (
					<img key={alt} src={src} alt={alt} style={{ height: 28, width: "auto" }} />
				))}
			</div>

			{/* Stripe notice */}
			<Typography variant="caption" align="center" color="text.secondary">
				Secure checkout powered by{" "}
				<span style={{ color: "#635BFF", fontWeight: 700 }}>Stripe</span>
			</Typography>
		</div>
	);

	const removeContent = (
		<RemoveRecordDialog
			petName={petName}
			chip={chip}
			email={email}
			onCancel={() => setView("activate")}
			onDone={() => {
				setView("activate");
				onRemove?.();
			}}
		/>
	);

	const activeContent = view === "activate" ? content : removeContent;

	return (
		<Dialog
			open
			fullScreen={isMobile}
			maxWidth="sm"
			fullWidth
			disableEscapeKeyDown
			PaperProps={{
				sx: isMobile
					? { borderRadius: 0, px: 2, py: 3 }
					: { borderRadius: 3, px: 2, py: 0.5, maxWidth: 640 },
			}}
		>
			<DialogContent>
				{isMobile ? <div className="max-w-sm mx-auto">{activeContent}</div> : activeContent}
			</DialogContent>
		</Dialog>
	);
}

export default RecoverActivateModal;
